// Comprehensive JWT token validation for AWS Cognito tokens
// Handles both access tokens and ID tokens properly

export interface TokenPayload {
  sub: string;
  exp: number;
  iat: number;
  token_use?: 'access' | 'id';
  aud?: string;
  client_id?: string;
  email?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
}

export class CognitoTokenValidator {
  /**
   * Validate any Cognito token (access or ID token)
   */
  static validateToken(token: string | any): boolean {
    try {
      const tokenString = typeof token === 'string' ? token : token?.toString();
      
      if (!tokenString) {
        return false;
      }

      // Check JWT structure
      const parts = tokenString.split('.');
      if (parts.length !== 3) {
        return false;
      }

      const payload = this.parseTokenPayload(tokenString);
      if (!payload) {
        return false;
      }

      // Check expiration
      if (!this.isTokenNotExpired(payload)) {
        return false;
      }

      // Check required fields
      if (!payload.sub || !payload.exp || !payload.iat) {
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Token validation failed:', error);
      return false;
    }
  }

  /**
   * Validate specifically an access token
   */
  static validateAccessToken(token: string | any): boolean {
    if (!this.validateToken(token)) {
      return false;
    }

    try {
      const payload = this.parseTokenPayload(typeof token === 'string' ? token : token.toString());
      
      // Access tokens should have token_use: 'access' and client_id
      return payload?.token_use === 'access' && !!payload.client_id;
    } catch {
      return false;
    }
  }

  /**
   * Validate specifically an ID token
   */
  static validateIdToken(token: string | any): boolean {
    if (!this.validateToken(token)) {
      return false;
    }

    try {
      const payload = this.parseTokenPayload(typeof token === 'string' ? token : token.toString());
      
      // ID tokens should have token_use: 'id' and aud (audience)
      return payload?.token_use === 'id' && !!payload.aud;
    } catch {
      return false;
    }
  }

  /**
   * Parse JWT token payload safely
   */
  static parseTokenPayload(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.warn('Failed to parse token payload:', error);
      return null;
    }
  }

  /**
   * Check if token is not expired (with buffer)
   */
  static isTokenNotExpired(payload: TokenPayload, bufferMinutes: number = 5): boolean {
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = bufferMinutes * 60;
    return payload.exp > (currentTime + bufferTime);
  }

  /**
   * Get user information from ID token
   */
  static getUserFromIdToken(idToken: string): any {
    const payload = this.parseTokenPayload(idToken);
    if (!payload || payload.token_use !== 'id') {
      return null;
    }

    return {
      id: payload.sub,
      email: payload.email,
      username: payload['cognito:username'] || payload.email,
      groups: payload['cognito:groups'] || [],
    };
  }

  /**
   * Check if token needs refresh (within 5 minutes of expiry)
   */
  static needsRefresh(token: string, bufferMinutes: number = 5): boolean {
    const payload = this.parseTokenPayload(token);
    if (!payload) return true;
    
    return !this.isTokenNotExpired(payload, bufferMinutes);
  }

  /**
   * Validate a session with both access and ID tokens
   */
  static validateSession(accessToken: string | any, idToken: string | any): boolean {
    const validAccessToken = this.validateAccessToken(accessToken);
    const validIdToken = this.validateIdToken(idToken);
    
    if (!validAccessToken || !validIdToken) {
      console.warn('Session validation failed:', {
        validAccessToken,
        validIdToken
      });
      return false;
    }

    return true;
  }
}

// Export utility functions
export const validateToken = (token: string | any) => CognitoTokenValidator.validateToken(token);
export const validateAccessToken = (token: string | any) => CognitoTokenValidator.validateAccessToken(token);
export const validateIdToken = (token: string | any) => CognitoTokenValidator.validateIdToken(token);
export const parseTokenPayload = (token: string) => CognitoTokenValidator.parseTokenPayload(token);
export const validateSession = (accessToken: string | any, idToken: string | any) => 
  CognitoTokenValidator.validateSession(accessToken, idToken);
